import React, { useState, useMemo } from "react"
import {
    Eye, MessageSquare, Archive as ArchiveIcon, Search,
    Sparkles, PhoneCall, Calendar, FileText, UserCheck, GraduationCap,
    Archive, ChevronDown, Phone, Clock, Star, X, CheckCircle2
} from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { updateLead, normalizeStatus } from "@/lib/supabase"
import { LeadLevelAvatar } from "./lead-level-avatar"
import { getLastInteractionDateOrEpoch } from "@/lib/dates"

// Estados con diseño espectacular
const STATUS_OPTIONS = [
    {
        value: "nuevo",
        label: "Primer Contacto",
        emoji: "✨",
        icon: Sparkles,
        color: "from-cyan-400 to-blue-500",
        bgLight: "bg-cyan-50 dark:bg-cyan-900/30",
        textColor: "text-cyan-700 dark:text-cyan-300",
        borderColor: "border-cyan-300 dark:border-cyan-700"
    },
    {
        value: "primer_contacto",
        label: "Proceso",
        emoji: "📞",
        icon: PhoneCall,
        color: "from-sky-400 to-blue-600",
        bgLight: "bg-sky-50 dark:bg-sky-900/30",
        textColor: "text-sky-700 dark:text-sky-300",
        borderColor: "border-sky-300 dark:border-sky-700"
    },
    {
        value: "agendar_cita",
        label: "Cita Agendada",
        emoji: "📅",
        icon: Calendar,
        color: "from-amber-400 to-orange-500",
        bgLight: "bg-amber-50 dark:bg-amber-900/30",
        textColor: "text-amber-700 dark:text-amber-300",
        borderColor: "border-amber-300 dark:border-amber-700"
    },
    {
        value: "aplicar_examen",
        label: "Examen",
        emoji: "📝",
        icon: FileText,
        color: "from-emerald-400 to-teal-500",
        bgLight: "bg-emerald-50 dark:bg-emerald-900/30",
        textColor: "text-emerald-700 dark:text-emerald-300",
        borderColor: "border-emerald-300 dark:border-emerald-700"
    },
    {
        value: "cierre_inscripcion",
        label: "Cierre",
        emoji: "✅",
        icon: UserCheck,
        color: "from-indigo-400 to-purple-500",
        bgLight: "bg-indigo-50 dark:bg-indigo-900/30",
        textColor: "text-indigo-700 dark:text-indigo-300",
        borderColor: "border-indigo-300 dark:border-indigo-700"
    },
    {
        value: "inscrito",
        label: "Inscrito",
        emoji: "🎓",
        icon: GraduationCap,
        color: "from-violet-400 to-purple-600",
        bgLight: "bg-violet-50 dark:bg-violet-900/30",
        textColor: "text-violet-700 dark:text-violet-300",
        borderColor: "border-violet-300 dark:border-violet-700"
    },
    {
        value: "archivado",
        label: "Archivado",
        emoji: "📁",
        icon: Archive,
        color: "from-rose-400 to-red-500",
        bgLight: "bg-rose-50 dark:bg-rose-900/30",
        textColor: "text-rose-700 dark:text-rose-300",
        borderColor: "border-rose-300 dark:border-rose-700"
    },
]

const STATUS_MAP = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s.value] = s
    return acc
}, {})

// Helper para obtener iniciales
const getInitials = (name) => {
    if (!name) return "?"
    const parts = name.trim().split(" ")
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
}

/**
 * KanbanColumnMobile
 * Vista compacta y táctil por columna para móviles - DISEÑO ESPECTACULAR
 */
export default function KanbanColumnMobile({
    column,
    leads = [],
    onViewDetails,
    onOpenConversation,
    onArchive,
}) {
    const [query, setQuery] = useState("")
    const [page, setPage] = useState(1)
    const [statusModalLead, setStatusModalLead] = useState(null)
    const [saving, setSaving] = useState(false)
    const PER_PAGE = 8

    const filtered = useMemo(() => {
        const q = String(query || "").trim().toLowerCase()
        if (!q) return leads
        return leads.filter((l) =>
            (l.nombre_tutor || "").toLowerCase().includes(q) ||
            (l.telefono || "").includes(q) ||
            (l.email || "").toLowerCase().includes(q) ||
            (String(l.tags || "") || "").toLowerCase().includes(q)
        )
    }, [leads, query])

    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE))
    const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE)

    const formatDays = (lead) => {
        const last = getLastInteractionDateOrEpoch(lead)
        const now = new Date()
        const diff = Math.ceil((now - last) / (1000 * 60 * 60 * 24))
        if (diff === 0) return "Hoy"
        if (diff === 1) return "Ayer"
        return `${diff}d`
    }

    const getInactivityBadge = (lead) => {
        const last = getLastInteractionDateOrEpoch(lead)
        const now = new Date()
        const diff = Math.ceil((now - last) / (1000 * 60 * 60 * 24))
        if (diff >= 5) return { label: formatDays(lead), className: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300" }
        if (diff >= 3) return { label: formatDays(lead), className: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" }
        return { label: formatDays(lead), className: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" }
    }

    const handleArchive = async (lead) => {
        try {
            await updateLead(lead.id, {
                estado_lead: "archivado",
                archivado: true,
                archived_at: new Date().toISOString(),
                archived_reason: "Archivado desde vista móvil",
            })
            onArchive?.(lead.id)
        } catch (err) {
            console.warn("[KanbanMobile] archive error", err)
            onArchive?.(lead.id)
        }
    }

    const handleChangeStatus = async (lead, newStatus) => {
        if (!lead || !newStatus) return
        setSaving(true)
        try {
            await updateLead(lead.id, {
                estado_lead: newStatus,
            })
            if (newStatus === "archivado") {
                onArchive?.(lead.id)
            }
            setStatusModalLead(null)
        } catch (err) {
            console.warn("[KanbanMobile] change status failed", err)
        } finally {
            setSaving(false)
        }
    }

    const getCurrentStatus = (lead) => {
        const normalized = normalizeStatus(lead.estado_lead)
        return STATUS_MAP[normalized] || STATUS_OPTIONS[0]
    }

    const ColumnIcon = column?.icon || Sparkles

    return (
        <div className="p-3 md:hidden">
            {/* Header de columna con gradiente */}
            <div className="mb-4 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-slate-800 dark:via-slate-700 dark:to-slate-800 p-4 shadow-xl">
                <div className="flex items-center gap-3">
                    <div
                        className="h-12 w-12 rounded-xl flex items-center justify-center shadow-lg"
                        style={{
                            background: `linear-gradient(135deg, ${column?.accent || '#6366f1'}80, ${column?.accent || '#6366f1'}40)`
                        }}
                    >
                        <ColumnIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-xl font-bold text-white">{column.title}</h3>
                        <p className="text-sm text-slate-300">{leads.length} leads en esta etapa</p>
                    </div>
                </div>
            </div>

            {/* Barra de búsqueda mejorada */}
            <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                    type="text"
                    placeholder="Buscar lead, tutor o teléfono..."
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setPage(1) }}
                    className="w-full rounded-xl border-2 border-slate-200 bg-white pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/50 focus:border-indigo-400 dark:bg-slate-800/80 dark:border-slate-700 dark:focus:border-indigo-500 placeholder:text-slate-400 shadow-sm transition-all"
                />
                {query && (
                    <button
                        onClick={() => setQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                        <X className="h-4 w-4 text-slate-400" />
                    </button>
                )}
            </div>

            {/* Lista de leads con diseño espectacular */}
            <div className="space-y-3">
                {pageItems.map((lead) => {
                    const currentStatus = getCurrentStatus(lead)
                    const StatusIcon = currentStatus.icon
                    const badge = getInactivityBadge(lead)

                    return (
                        <div
                            key={lead.id}
                            className="relative overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-md transition-all duration-200"
                        >
                            <div className="p-3.5">
                                {/* Header: Avatar + Info + Badge tiempo */}
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="relative flex-shrink-0">
                                        <LeadLevelAvatar
                                            nivel={lead.nivel_educativo}
                                            size={48}
                                            initials={getInitials(lead.nombre_tutor || lead.nombre)}
                                        />
                                        {lead.prioridad === "alta" && (
                                            <div className="absolute -top-1 -right-1 bg-amber-400 rounded-full p-1 shadow-md">
                                                <Star className="h-3 w-3 text-white fill-white" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-base font-bold text-slate-900 dark:text-white truncate">
                                            {lead.nombre_tutor || "Sin nombre"}
                                        </h4>
                                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                                            <Phone className="h-3.5 w-3.5" />
                                            <span className="truncate">{lead.telefono || "Sin teléfono"}</span>
                                        </div>
                                        {lead.nivel_educativo && (
                                            <div className="flex items-center gap-1.5 mt-1">
                                                <GraduationCap className="h-3.5 w-3.5 text-indigo-500" />
                                                <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 capitalize">
                                                    {lead.nivel_educativo}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Badge de tiempo */}
                                    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${badge.className}`}>
                                        <Clock className="h-3 w-3" />
                                        {badge.label}
                                    </div>
                                </div>

                                {/* Selector de estado - Botón bonito */}
                                <button
                                    onClick={() => setStatusModalLead(lead)}
                                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border-2 ${currentStatus.borderColor} ${currentStatus.bgLight} transition-all hover:shadow-md active:scale-[0.98]`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${currentStatus.color} flex items-center justify-center shadow-sm`}>
                                            <StatusIcon className="h-4 w-4 text-white" />
                                        </div>
                                        <span className={`text-sm font-semibold ${currentStatus.textColor}`}>
                                            {currentStatus.emoji} {currentStatus.label}
                                        </span>
                                    </div>
                                    <ChevronDown className={`h-5 w-5 ${currentStatus.textColor}`} />
                                </button>

                                {/* Acciones rápidas — minimal */}
                                <div className="flex items-center gap-2 mt-2.5">
                                    <button
                                        onClick={() => onOpenConversation?.(lead)}
                                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold active:scale-95 transition-all shadow-sm"
                                    >
                                        <MessageSquare className="h-3.5 w-3.5" />
                                        Chat
                                    </button>

                                    <button
                                        onClick={() => onViewDetails?.(lead)}
                                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-semibold active:scale-95 transition-all hover:bg-slate-200 dark:hover:bg-slate-700"
                                    >
                                        <Eye className="h-3.5 w-3.5" />
                                        Ver
                                    </button>

                                    <button
                                        onClick={() => handleArchive(lead)}
                                        className="flex items-center justify-center p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 active:scale-95 transition-all hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-900/30 dark:hover:text-rose-400"
                                        title="Archivar"
                                    >
                                        <ArchiveIcon className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )
                })}

                {filtered.length === 0 && (
                    <div className="py-12 text-center rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 mb-4">
                            <Search className="h-8 w-8 text-slate-400" />
                        </div>
                        <p className="text-sm font-medium text-slate-500">Sin leads que coincidan</p>
                        <p className="text-xs text-slate-400 mt-1">Intenta con otro término de búsqueda</p>
                    </div>
                )}
            </div>

            {/* Paginación mejorada */}
            {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                    <Button
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        variant="outline"
                        size="sm"
                        className="rounded-xl"
                    >
                        Anterior
                    </Button>
                    <div className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800">
                        <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{page}</span>
                        <span className="text-xs text-slate-400">/</span>
                        <span className="text-sm text-slate-500">{totalPages}</span>
                    </div>
                    <Button
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        variant="outline"
                        size="sm"
                        className="rounded-xl"
                    >
                        Siguiente
                    </Button>
                </div>
            )}

            {/* Modal de cambio de estado - Bottom Sheet Redesigned */}
            {statusModalLead && (
                <div className="fixed inset-0 z-[80] flex items-end justify-center">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-[6px]"
                        onClick={() => setStatusModalLead(null)}
                    />
                    <div className="relative w-full max-w-lg rounded-t-[28px] bg-white dark:bg-slate-900 shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[80vh] overflow-hidden flex flex-col">
                        {/* Drag indicator */}
                        <div className="flex justify-center pt-3 pb-1">
                            <div className="w-10 h-1 bg-slate-200 dark:bg-slate-700 rounded-full" />
                        </div>

                        {/* Header — compact */}
                        <div className="flex items-center gap-3 px-5 pb-3 pt-1">
                            <LeadLevelAvatar
                                nivel={statusModalLead.nivel_educativo}
                                size={40}
                                initials={getInitials(statusModalLead.nombre_tutor || statusModalLead.nombre)}
                            />
                            <div className="flex-1 min-w-0">
                                <p className="font-bold text-base text-slate-900 dark:text-white truncate">
                                    {statusModalLead.nombre_tutor || "Sin nombre"}
                                </p>
                                <p className="text-xs text-slate-400 flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {statusModalLead.telefono || "Sin teléfono"}
                                </p>
                            </div>
                            <button
                                onClick={() => setStatusModalLead(null)}
                                className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 active:scale-90 transition-transform"
                            >
                                <X className="h-4 w-4 text-slate-400" />
                            </button>
                        </div>

                        {/* Content — compact grid */}
                        <div className="flex-1 overflow-y-auto px-4 pb-2">
                            <p className="text-[10px] font-bold text-slate-400 mb-2.5 uppercase tracking-widest px-1">
                                Mover a
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                                {STATUS_OPTIONS.map((status) => {
                                    const isActive = normalizeStatus(statusModalLead.estado_lead) === status.value
                                    const StatusIcon = status.icon
                                    return (
                                        <button
                                            key={status.value}
                                            onClick={() => handleChangeStatus(statusModalLead, status.value)}
                                            disabled={saving}
                                            className={`relative flex flex-col items-center gap-1.5 p-3 rounded-2xl text-center transition-all active:scale-[0.96] ${isActive
                                                ? "bg-white dark:bg-slate-800 shadow-lg ring-2 ring-emerald-400 dark:ring-emerald-500"
                                                : "bg-slate-50/80 dark:bg-slate-800/40 hover:bg-white dark:hover:bg-slate-800 hover:shadow-md"
                                                }`}
                                        >
                                            <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${status.color} flex items-center justify-center shadow-md`}>
                                                <StatusIcon className="h-5 w-5 text-white" />
                                            </div>
                                            <span className={`text-xs font-semibold leading-tight ${isActive ? "text-slate-900 dark:text-white" : "text-slate-600 dark:text-slate-400"}`}>
                                                {status.label}
                                            </span>
                                            {isActive && (
                                                <div className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full bg-emerald-500 flex items-center justify-center">
                                                    <CheckCircle2 className="h-3 w-3 text-white" />
                                                </div>
                                            )}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800 safe-area-bottom">
                            <button
                                onClick={() => setStatusModalLead(null)}
                                className="w-full py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-semibold text-slate-500 dark:text-slate-400 active:scale-[0.98] transition-transform"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
